import React, { FC, ReactNode } from "react";
import { createStyles, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";

type PreviewDisplayProps = {
  value?: string | number;
  title?: string;
  bold?: boolean;
  variant?: "column" | "row";
  childVariant?: "column" | "row";
  height?: number;
  width?: number | string;
  labelWidth?: string;
  warning?: boolean;
  noWhitespace?: boolean;
  type?: "string" | "date" | "amount" | "zeroOK";
  unit?: string;
  childAlign?: "center";
  children?: ReactNode;
};

const useStyles = makeStyles<Theme, PreviewDisplayProps>((theme: Theme) =>
  createStyles({
    container: (props: PreviewDisplayProps) => ({
      display: "flex",
      flexDirection: props.variant || "row",
      justifyContent: "flex-start",
      alignItems: props.childAlign
        ? "center"
        : props.variant === "row"
          ? "center"
          : props.variant === "column"
            ? "flex-start"
            : "baseline",
      gridColumn: props.width ? `span ${props.width} / span ${props.width}` : "auto",
      gridRow: props.height ? `span ${props.height} / span ${props.height}` : "auto",
    }),
    title: (props: PreviewDisplayProps) => ({
      margin: 0,
      fontSize: "0.8rem",
      fontWeight: 500,
      color: theme.palette.secondary.main,
      marginRight: "1rem",
      marginBottom: props.variant ? "0.5rem" : 0,
      width: props.labelWidth && props.variant === "row" ? props.labelWidth : "auto",
      lineHeight: 1,
    }),
    value: (props: PreviewDisplayProps) => ({
      margin: 0,
      fontSize: "0.875rem",
      lineHeight: 1,
      fontWeight: props.bold ? 500 : 400,
    }),
    child: (props: PreviewDisplayProps) => ({
      display: "flex",
      alignItems: "baseline",
      flexDirection: props.childVariant || "row",
      gap: "0.25rem",
    }),
    warning: {
      color: theme.palette.error.main,
    },
    noWhitespace: {
      whiteSpace: "nowrap",
    },
    unit: {
      fontSize: "0.75rem",
      lineHeight: 1,
    },
  })
);

const PreviewDisplay: FC<PreviewDisplayProps> = ({
  value = "",
  title = "",
  bold = false,
  variant = "column",
  childVariant,
  height = 1,
  width = 1,
  labelWidth = "150px",
  warning = false,
  children,
  noWhitespace = false,
  type,
  unit,
  childAlign,
}) => {
  const classes = useStyles({
    value,
    title,
    bold,
    variant,
    childVariant,
    height,
    width,
    labelWidth,
    warning,
    noWhitespace,
    type,
    unit,
    childAlign,
  });

  let formatted = "";

  if (Array.isArray(value)) {
    formatted = value.join(", ");
  } else if (type === "amount" && value) {
    formatted = Number(value).toLocaleString("fi-FI");
  } else if (type === "date" && typeof value === "string") {
    formatted = value; // Could add date formatting here if needed
  } else if (type === "string" && value) {
    formatted = value.toString();
  } else if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "bigint" ||
    typeof value === "boolean"
  ) {
    formatted = value.toString();
  }

  return (
    <div className={classes.container}>
      <h3 className={classes.title}>{title || " "}</h3>

      {value ? (
        <p
          className={clsx(
            classes.value,
            warning && classes.warning,
            noWhitespace && classes.noWhitespace
          )}
        >
          {formatted || "-"}
          <span className={classes.unit}>{unit ? ` ${unit}` : ""}</span>
        </p>
      ) : null}

      {children && (
        <div className={clsx(classes.value, classes.child, noWhitespace && classes.noWhitespace)}>
          {children}
        </div>
      )}

      {!children && !value && type !== "zeroOK" && <p className={classes.value}>-</p>}

      {type === "zeroOK" && !value && (
        <p className={classes.value}>
          0<span className={classes.unit}>{unit ? ` ${unit}` : ""}</span>
        </p>
      )}
    </div>
  );
};

export default PreviewDisplay;
